import {
    getNumberValue,
    getStringValue,
    isRecord,
} from '@apps/web/components/phoebe_chat/record_utils';
import { parseStructuredReportFromOutput } from '@apps/web/components/phoebe_chat_view/artifacts/structured_report_artifacts';
import { formatCadenceLabel } from '@apps/web/components/phoebe_chat_view/tool_calls/conversation_control_tool_ui';
import {
    getOutreachShifts,
    joinNameParts,
} from '@apps/web/components/phoebe_chat_view/tool_calls/detail_tool_ui';
import {
    getActionChangedFields,
    getActionError,
    getAvailabilityWindow,
} from '@apps/web/components/phoebe_chat_view/tool_calls/record_settings_tool_ui';
import { getSkillContextHeaderDetail } from '@apps/web/components/phoebe_chat_view/tool_calls/skill_context_tool_ui';
import {
    formatDetailStatus,
    joinPresent,
} from '@apps/web/components/phoebe_chat_view/tool_calls/tool_call_template';
import { type ToolExecutionEntry } from '@apps/web/components/phoebe_chat_view/transcript_types';

export function getToolResultReviewMessage(output: unknown): string | null {
    if (!isRecord(output)) {
        return null;
    }
    return getActionError(output) ?? null;
}

export function getToolApprovalBadge(entry: ToolExecutionEntry): {
    readonly label: string;
    readonly variant: 'success' | 'destructive';
} | null {
    switch (entry.approvalState) {
        case 'pending':
            return { label: 'Approval pending', variant: 'success' };
        case 'approved':
            return { label: 'Tool approved', variant: 'success' };
        case 'denied':
            return {
                label: 'Denied — not performed',
                variant: 'destructive',
            };
        default:
            return null;
    }
}

function getCountDetail({
    data,
    countKeys,
    collectionKeys = [],
    singular,
    plural,
    emptyLabel,
}: {
    readonly data: Record<string, unknown>;
    readonly countKeys: ReadonlyArray<string>;
    readonly collectionKeys?: ReadonlyArray<string>;
    readonly singular: string;
    readonly plural: string;
    readonly emptyLabel?: string;
}): string | null {
    let count: number | null = null;
    for (const key of countKeys) {
        const value = data[key];
        if (typeof value === 'number' && Number.isFinite(value)) {
            count = value;
            break;
        }
    }

    if (count === null) {
        for (const key of collectionKeys) {
            const value = data[key];
            if (Array.isArray(value)) {
                count = value.length;
                break;
            }
        }
    }

    if (count === null) {
        return null;
    }

    if (count === 0) {
        return emptyLabel ?? `No ${plural} found`;
    }

    return `${count} ${count === 1 ? singular : plural} found`;
}

function getCaregiversCheckedDetail(
    data: Record<string, unknown>,
): string | null {
    const detail = getCountDetail({
        data,
        countKeys: ['found_count'],
        collectionKeys: ['results'],
        singular: 'caregiver',
        plural: 'caregivers',
        emptyLabel: 'No caregivers checked',
    });

    return detail?.replace(/ found$/, ' checked') ?? null;
}

function getRecordActionHeaderDetail(entry: ToolExecutionEntry): string | null {
    if (!isRecord(entry.output)) {
        return null;
    }
    if (getActionError(entry.output)) {
        return 'Needs review';
    }
    const fields = getActionChangedFields(
        entry.output,
        isRecord(entry.arguments) ? entry.arguments : null,
    );
    return fields.length > 0
        ? `${fields.length} field${fields.length === 1 ? '' : 's'} updated`
        : 'Updated';
}

function getAvailabilityActionHeaderDetail(
    entry: ToolExecutionEntry,
): string | null {
    if (!isRecord(entry.output)) {
        return null;
    }
    if (getActionError(entry.output)) {
        return 'Needs review';
    }
    if (entry.name === 'delete_caregiver_availability') {
        const removedRows = getNumberValue(entry.output, 'removed_rows');
        return removedRows !== null
            ? `${removedRows} row${removedRows === 1 ? '' : 's'} removed`
            : 'Removed';
    }
    return (
        getAvailabilityWindow(
            entry.name,
            entry.output,
            isRecord(entry.arguments) ? entry.arguments : null,
        ) ?? 'Saved'
    );
}

function getMemoryActionHeaderDetail(entry: ToolExecutionEntry): string | null {
    if (!isRecord(entry.output)) {
        return null;
    }
    if (getActionError(entry.output)) {
        return 'Needs review';
    }
    const characterCount =
        getNumberValue(entry.output, 'character_count') ??
        getNumberValue(entry.output, 'content_length');
    if (characterCount !== null) {
        return `${characterCount} character${characterCount === 1 ? '' : 's'}`;
    }
    return 'Saved';
}

function getSaveNoteHeaderDetail(entry: ToolExecutionEntry): string | null {
    const output = isRecord(entry.output) ? entry.output : null;

    if (output && getActionError(output)) {
        return 'Needs review';
    }

    return 'Saved';
}

type ToolExecutionHeaderDetailBuilder = (
    entry: ToolExecutionEntry,
) => string | null;

const TOOL_EXECUTION_HEADER_DETAIL_BUILDERS: Record<
    string,
    ToolExecutionHeaderDetailBuilder
> = {
    caregivers_details: (entry) =>
        isRecord(entry.output)
            ? getCountDetail({
                  data: entry.output,
                  countKeys: ['count'],
                  collectionKeys: ['caregivers'],
                  singular: 'caregiver profile',
                  plural: 'caregiver profiles',
                  emptyLabel: 'No caregiver profiles returned',
              })
            : null,
    client_details: (entry) =>
        isRecord(entry.output)
            ? (getStringValue(entry.output, 'name') ??
              joinNameParts(
                  getStringValue(entry.output, 'first_name'),
                  getStringValue(entry.output, 'last_name'),
              ))
            : null,
    caregivers_search: (entry) =>
        isRecord(entry.output)
            ? getCountDetail({
                  data: entry.output,
                  countKeys: ['count'],
                  collectionKeys: ['caregivers'],
                  singular: 'caregiver',
                  plural: 'caregivers',
                  emptyLabel: 'No caregivers found',
              })
            : null,
    clients_search: (entry) =>
        isRecord(entry.output)
            ? getCountDetail({
                  data: entry.output,
                  countKeys: ['count'],
                  collectionKeys: ['clients'],
                  singular: 'client',
                  plural: 'clients',
                  emptyLabel: 'No clients found',
              })
            : null,
    get_client_details: (entry) =>
        isRecord(entry.output)
            ? joinNameParts(
                  getStringValue(entry.output, 'first_name'),
                  getStringValue(entry.output, 'last_name'),
              )
            : null,
    get_outreach_details: (entry) =>
        isRecord(entry.output)
            ? getCountDetail({
                  data: entry.output,
                  countKeys: [],
                  collectionKeys: ['shifts'],
                  singular: 'shift',
                  plural: 'shifts',
                  emptyLabel: 'No shifts returned',
              })
            : null,
    shifts_search: (entry) =>
        isRecord(entry.output)
            ? getCountDetail({
                  data: entry.output,
                  countKeys: ['count'],
                  collectionKeys: ['shifts'],
                  singular: 'shift',
                  plural: 'shifts',
                  emptyLabel: 'No shifts found',
              })
            : null,
    get_shifts: (entry) =>
        isRecord(entry.output)
            ? getCountDetail({
                  data: entry.output,
                  countKeys: ['row_count'],
                  singular: 'shift',
                  plural: 'shifts',
                  emptyLabel: 'No shifts found',
              })
            : null,
    shifts_details: (entry) => {
        if (!isRecord(entry.output)) {
            return null;
        }
        const patient = isRecord(entry.output['patient'])
            ? entry.output['patient']
            : null;
        const timing = isRecord(entry.output['timing'])
            ? entry.output['timing']
            : null;
        return joinPresent([
            getStringValue(patient, 'name'),
            getStringValue(timing, 'date'),
        ]);
    },
    outreach_search: (entry) =>
        isRecord(entry.output)
            ? getCountDetail({
                  data: entry.output,
                  countKeys: ['total_count', 'count'],
                  collectionKeys: ['callouts'],
                  singular: 'outreach item',
                  plural: 'outreach items',
                  emptyLabel: 'No outreach found',
              })
            : null,
    callouts_details: (entry) => {
        if (!isRecord(entry.output)) {
            return null;
        }
        const shifts = getOutreachShifts(entry.output);
        const status =
            getStringValue(entry.output, 'status') ??
            getStringValue(entry.output, 'outreach_status');
        return joinPresent([
            status ? formatDetailStatus(status) : null,
            shifts.length > 0
                ? `${shifts.length} shift${shifts.length === 1 ? '' : 's'}`
                : null,
        ]);
    },
    callouts_search: (entry) =>
        isRecord(entry.output)
            ? getCountDetail({
                  data: entry.output,
                  countKeys: ['count'],
                  collectionKeys: ['callouts'],
                  singular: 'open shift',
                  plural: 'open shifts',
                  emptyLabel: 'No open shifts found',
              })
            : null,
    search_conversation_history: (entry) =>
        isRecord(entry.output)
            ? getCountDetail({
                  data: entry.output,
                  countKeys: ['count'],
                  collectionKeys: ['results'],
                  singular: 'message',
                  plural: 'messages',
                  emptyLabel: 'No messages found',
              })
            : null,
    search_voice_call_transcripts: (entry) =>
        isRecord(entry.output)
            ? getCountDetail({
                  data: entry.output,
                  countKeys: ['count'],
                  collectionKeys: ['results'],
                  singular: 'transcript',
                  plural: 'transcripts',
                  emptyLabel: 'No transcripts found',
              })
            : null,
    search_caregiver_memories: (entry) =>
        isRecord(entry.output)
            ? getCountDetail({
                  data: entry.output,
                  countKeys: ['count'],
                  collectionKeys: ['results'],
                  singular: 'caregiver',
                  plural: 'caregivers',
                  emptyLabel: 'No caregivers found',
              })
            : null,
    get_shift_recommendations: (entry) =>
        isRecord(entry.output)
            ? getCountDetail({
                  data: entry.output,
                  countKeys: ['count', 'recommendation_count'],
                  collectionKeys: ['recommendations'],
                  singular: 'recommendation',
                  plural: 'recommendations',
                  emptyLabel: 'No recommendations found',
              })
            : null,
    get_recommendations: (entry) =>
        isRecord(entry.output)
            ? getCountDetail({
                  data: entry.output,
                  countKeys: ['count', 'recommendation_count'],
                  collectionKeys: ['recommendations'],
                  singular: 'recommendation',
                  plural: 'recommendations',
                  emptyLabel: 'No recommendations found',
              })
            : null,
    get_group_recommendations: (entry) =>
        isRecord(entry.output)
            ? getCountDetail({
                  data: entry.output,
                  countKeys: ['recommendation_count', 'count'],
                  collectionKeys: ['recommendations'],
                  singular: 'recommendation',
                  plural: 'recommendations',
                  emptyLabel: 'No recommendations found',
              })
            : null,
    get_caregiver_recommendation_details: (entry) =>
        isRecord(entry.output)
            ? getCaregiversCheckedDetail(entry.output)
            : null,
    get_caregiver_group_recommendation_details: (entry) =>
        isRecord(entry.output)
            ? getCaregiversCheckedDetail(entry.output)
            : null,
    group_shifts_for_auto_draft: (entry) =>
        isRecord(entry.output)
            ? getCountDetail({
                  data: entry.output,
                  countKeys: ['group_count'],
                  collectionKeys: ['groups'],
                  singular: 'auto-draft group',
                  plural: 'auto-draft groups',
                  emptyLabel: 'No auto-draft groups',
              })
            : null,
    get_outreach_suggestion_drafts: (entry) => {
        if (!isRecord(entry.output)) {
            return null;
        }
        const unitDrafts = Array.isArray(entry.output['unit_drafts'])
            ? entry.output['unit_drafts']
            : [];
        if (unitDrafts.length === 0) {
            return 'No draft units';
        }
        const suggestionCount = unitDrafts.reduce((total, draft) => {
            if (!isRecord(draft)) {
                return total;
            }
            const count = draft['suggestion_count'];
            if (typeof count === 'number' && Number.isFinite(count)) {
                return total + count;
            }
            const selectedSuggestions = draft['selected_suggestions'];
            return Array.isArray(selectedSuggestions)
                ? total + selectedSuggestions.length
                : total;
        }, 0);
        const unitLabel = `${unitDrafts.length} unit${unitDrafts.length === 1 ? '' : 's'}`;
        return `${suggestionCount} suggestion${suggestionCount === 1 ? '' : 's'} across ${unitLabel}`;
    },
    update_caregiver_record: getRecordActionHeaderDetail,
    update_client_record: getRecordActionHeaderDetail,
    update_client_phone_preference: getRecordActionHeaderDetail,
    add_caregiver_weekly_availability: getAvailabilityActionHeaderDetail,
    add_caregiver_one_off_availability: getAvailabilityActionHeaderDetail,
    update_caregiver_availability: getAvailabilityActionHeaderDetail,
    delete_caregiver_availability: getAvailabilityActionHeaderDetail,
    save_caregiver_memory_general: getMemoryActionHeaderDetail,
    save_caregiver_memory_instruction: getMemoryActionHeaderDetail,
    save_client_memory: getMemoryActionHeaderDetail,
    save_client_memory_general: getMemoryActionHeaderDetail,
    save_client_memory_instruction: getMemoryActionHeaderDetail,
    save_note: getSaveNoteHeaderDetail,
    organization_scheduling_context: (entry) =>
        isRecord(entry.output) && isRecord(entry.output['organization'])
            ? getStringValue(entry.output['organization'], 'name')
            : null,
    monitor_outreach: (entry) => {
        if (!isRecord(entry.output)) {
            return null;
        }
        const cadenceLabel = formatCadenceLabel(
            getNumberValue(entry.output, 'cadence_seconds'),
        );
        const nextRound = getNumberValue(entry.output, 'next_round');
        return joinPresent([
            cadenceLabel,
            nextRound !== null ? `round ${nextRound} next` : null,
        ]);
    },
    stop_agent_monitoring: (entry) =>
        isRecord(entry.output)
            ? (getStringValue(entry.output, 'reason') ?? 'Monitoring stopped')
            : null,
    review_outreach: (entry) =>
        isRecord(entry.output) &&
        isRecord(entry.output['contact_attempts_summary'])
            ? getCountDetail({
                  data: entry.output['contact_attempts_summary'],
                  countKeys: ['total'],
                  collectionKeys: [],
                  singular: 'contact attempt',
                  plural: 'contact attempts',
                  emptyLabel: 'No contact attempts',
              })
            : null,
    notify: (entry) =>
        isRecord(entry.arguments)
            ? getStringValue(entry.arguments, 'title')
            : null,
    override_contact_attempt_response: (entry) =>
        isRecord(entry.output)
            ? formatDetailStatus(getStringValue(entry.output, 'response'))
            : null,
    submit_outreach_suggestion_feedback: (entry) =>
        isRecord(entry.output)
            ? formatDetailStatus(getStringValue(entry.output, 'sentiment'))
            : null,
    add_outreach_subscriber: (entry) =>
        isRecord(entry.output) ? getStringValue(entry.output, 'user_id') : null,
    remove_outreach_subscriber: (entry) =>
        isRecord(entry.output) ? getStringValue(entry.output, 'user_id') : null,
    approve_staged_message: (entry) =>
        isRecord(entry.output)
            ? getStringValue(entry.output, 'message_id')
            : null,
    reject_staged_message: (entry) =>
        isRecord(entry.output)
            ? getStringValue(entry.output, 'staged_message_id')
            : null,
    edit_staged_message_content: (entry) =>
        isRecord(entry.output)
            ? getStringValue(entry.output, 'staged_message_id')
            : null,
    send_one_off_sms_in_conversation: (entry) =>
        isRecord(entry.output)
            ? getStringValue(entry.output, 'message_id')
            : null,
    date_and_time: (entry) =>
        isRecord(entry.output)
            ? (getStringValue(entry.output, 'timezone') ??
              getStringValue(entry.output, 'current_time'))
            : null,
    get_current_datetime: (entry) =>
        isRecord(entry.output)
            ? (getStringValue(entry.output, 'timezone') ??
              getStringValue(entry.output, 'current_time'))
            : null,
    view_skills: (entry) =>
        getSkillContextHeaderDetail({
            toolName: entry.name,
            args: entry.arguments,
            output: entry.output,
        }),
    load_skill: (entry) =>
        getSkillContextHeaderDetail({
            toolName: entry.name,
            args: entry.arguments,
            output: entry.output,
        }),
    update_execution_plan: (entry) => {
        const args = isRecord(entry.arguments) ? entry.arguments : null;
        const items = Array.isArray(args?.['items']) ? args['items'] : [];
        return `${items.length} step${items.length === 1 ? '' : 's'}`;
    },
    query_outreach_report: (entry) => {
        const parsed = parseStructuredReportFromOutput(entry.output);
        return parsed
            ? `${parsed.summary.returned} row${parsed.summary.returned === 1 ? '' : 's'}`
            : null;
    },
};

export function getToolExecutionHeaderDetail(
    entry: ToolExecutionEntry,
): string | null {
    if (!entry.isComplete || entry.error) {
        return null;
    }
    return TOOL_EXECUTION_HEADER_DETAIL_BUILDERS[entry.name]?.(entry) ?? null;
}

export function getToolExecutionReviewMessage(
    entry: ToolExecutionEntry,
): string | null {
    if (entry.error) {
        return entry.error;
    }
    if (!entry.isComplete) {
        return null;
    }
    return getToolResultReviewMessage(entry.output);
}
